/**
 * Worker-run FEED contract — the keyset (cursor) "load more" history of the
 * autonomous agent fleet. Worker runs are the deepest, fastest-growing list in
 * the product (one row per agent run), append-only and time-ordered, so this is
 * a keyset feed (docs/design/LIST-PAGINATION-AUDIT.md), not an offset table:
 * `cursor` + `limit` in, `{ rows, next_cursor, has_more }` out, no total.
 *
 * Read-only. Runs are written by the runner; control actions live in
 * `run-trace.ts`. Worker runs are workspace-global (no workspace scope).
 */

import { z } from "zod";
import type { Operation } from "../types";
import { keysetQuery, keysetSchema } from "./_common";

/** One feed row — the worker-run columns the runs table renders (no control/steps). */
export const workerRunFeedRowSchema = z.object({
  id: z.number(),
  role: z.string(),
  goal_id: z.number().nullable(),
  goal_title: z.string().nullable(),
  agent_id: z.string(),
  status: z.enum(["running", "completed", "failed", "budget_exceeded", "aborted"]),
  exit_reason: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  tasks_claimed: z.number(),
  tasks_shipped: z.number(),
  tasks_verified: z.number(),
  loop_id: z.number().nullable(),
  variant_id: z.number().nullable(),
  input_tokens: z.number().nullable(),
  output_tokens: z.number().nullable(),
  cached_tokens: z.number().nullable(),
  cost_usd: z.number().nullable(),
  wall_ms: z.number().nullable(),
  model: z.string().nullable(),
  outcome: z.string().nullable(),
});
export type WorkerRunFeedRowDto = z.infer<typeof workerRunFeedRowSchema>;

export const agentRunsFeedQuery = keysetQuery.extend({
  role: z.string().optional(),
  goal_id: z.coerce.number().int().optional(),
});

export const agentRunsFeed: Operation<
  void,
  z.infer<typeof agentRunsFeedQuery>,
  void,
  z.infer<ReturnType<typeof keysetSchema<typeof workerRunFeedRowSchema>>>
> = {
  method: "GET",
  path: () => "/api/agents/runs",
  query: agentRunsFeedQuery,
  response: keysetSchema(workerRunFeedRowSchema),
};

/** Single-run detail for `/agents/runs/$id` (LiveRunTrace host). */
export const workerRunDetailSchema = z.object({
  id: z.number(),
  role: z.string(),
  goal_id: z.number().nullable(),
  goal_title: z.string().nullable(),
  agent_id: z.string(),
  status: z.enum(["running", "completed", "failed", "budget_exceeded", "aborted"]),
  exit_reason: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  tasks_claimed: z.number(),
  tasks_shipped: z.number(),
  tasks_verified: z.number(),
  cost_usd: z.number().nullable(),
  wall_ms: z.number().nullable(),
  model: z.string().nullable(),
  coding_agent: z.string().nullable(),
  model_provider: z.string().nullable(),
  provider: z.string().nullable(),
  control_state: z.string().nullable(),
  processHostLabel: z.string(),
  codingAgentLabel: z.string().nullable(),
});
export type WorkerRunDetailDto = z.infer<typeof workerRunDetailSchema>;

export const runEventDetailSchema = z.object({
  id: z.number(),
  seq: z.number(),
  kind: z.string(),
  tool_use_id: z.string().nullable(),
  tool_name: z.string().nullable(),
  body: z.string().nullable(),
  meta: z.string().nullable(),
  created_at: z.string(),
});

export const runStepDetailSchema = z.object({
  id: z.number(),
  worker_run_id: z.number(),
  step_id: z.number().nullable(),
  ordinal: z.number(),
  /** `excellent_run_steps.rung` is NOT NULL DEFAULT 1 (v38) and the join that
   *  reads it is a LEFT JOIN on the *right* table only — never nullable. */
  rung: z.number(),
  model: z.string().nullable(),
  input_tokens: z.number().nullable(),
  output_tokens: z.number().nullable(),
  cached_tokens: z.number().nullable(),
  wall_ms: z.number().nullable(),
  cost_usd: z.number().nullable(),
  tool_calls: z.number().nullable(),
  output_excerpt: z.string().nullable(),
  reflection: z.string().nullable(),
  attempt: z.number(),
  parent_step_id: z.number().nullable(),
  retry_of_step_id: z.number().nullable(),
  parallel_group_key: z.string().nullable(),
});

const runDetailResponse = z.object({
  run: workerRunDetailSchema,
  events: z.array(runEventDetailSchema),
  steps: z.array(runStepDetailSchema),
});
export type AgentRunDetailResponse = z.infer<typeof runDetailResponse>;

const agentRunsGet: Operation<{ id: number }, void, void, AgentRunDetailResponse> = {
  method: "GET",
  path: ({ id }) => `/api/agents/runs/${id}`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: runDetailResponse,
};

export const agentRuns = {
  feed: agentRunsFeed,
  get: agentRunsGet,
};
